// vg-binding: P7.D-29
//
// apps/vendor/src/features/inventory/TransferFormModal.tsx
//
// Phase 7 Wave 15 polish — vendor warehouse-to-warehouse transfer form.
// C13 (2026-05-16 sweep debug) — replaced the source <select> with a
// searchable picker, added warehouse names to the target list, and
// linked out to /settings/warehouses when only one warehouse exists.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button, Input } from '@printway/ui';

import { useTranslation } from '../../lib/i18n/index.js';
import {
  useCreateTransfer,
  useStockList,
  useWarehousesList,
  type InventoryApiError,
} from './api.js';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: (msg: string) => void;
}

function shortId(id: string): string {
  return id.length > 8 ? `…${id.slice(-8)}` : id;
}

export function TransferFormModal({ open, onClose, onSuccess }: Props): JSX.Element | null {
  const { t } = useTranslation('inventory');
  const stockQ = useStockList({ limit: 200 });
  const warehousesQ = useWarehousesList();
  const [stockId, setStockId] = useState('');
  const [sourceSearch, setSourceSearch] = useState('');
  const [showSourceDropdown, setShowSourceDropdown] = useState(false);
  const [targetWarehouseId, setTargetWarehouseId] = useState('');
  const [qtyStr, setQtyStr] = useState('');
  const [note, setNote] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const m = useCreateTransfer();

  useEffect(() => {
    if (!open) {
      setStockId('');
      setSourceSearch('');
      setShowSourceDropdown(false);
      setTargetWarehouseId('');
      setQtyStr('');
      setNote('');
      setErrorMsg(null);
    }
  }, [open]);

  const stockRows = useMemo(() => stockQ.data?.rows ?? [], [stockQ.data?.rows]);
  const sourceRow = useMemo(
    () => stockRows.find((r) => r.stock_id === stockId) ?? null,
    [stockRows, stockId],
  );

  // Typeahead filter — match against product name, variant label, sku,
  // and warehouse name. Falls back to id substring for legacy rows that
  // don't yet have the enriched labels.
  const filteredSourceRows = useMemo(() => {
    const q = sourceSearch.trim().toLowerCase();
    if (q.length === 0) return stockRows.slice(0, 50);
    return stockRows
      .filter((r) => {
        const hay = [
          r.product_name,
          r.variant_label,
          r.variant_sku,
          r.warehouse_name,
          r.variant_id,
        ]
          .filter((s): s is string => typeof s === 'string')
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 50);
  }, [stockRows, sourceSearch]);

  // Target warehouse choices = vendor's full warehouse list (active only,
  // excluding the source warehouse). dbg-r4 — was derived from stockRows,
  // which only surfaced warehouses that already had stock; transferring
  // INTO an empty warehouse was impossible because none of its rows
  // appeared in the source-stock listing.
  const allWarehouses = useMemo(
    () => warehousesQ.data?.rows ?? [],
    [warehousesQ.data?.rows],
  );
  const targetOptions = useMemo(() => {
    return allWarehouses
      .filter((w) => w.is_active)
      .filter((w) => !sourceRow || w.warehouse_id !== sourceRow.warehouse_id)
      .map((w) => ({ id: w.warehouse_id, label: w.name }));
  }, [allWarehouses, sourceRow]);

  if (!open) return null;

  const qty = Number.parseInt(qtyStr, 10);
  const qtyValid = Number.isFinite(qty) && qty >= 1;
  const canSubmit =
    sourceRow !== null &&
    targetWarehouseId.length === 24 &&
    qtyValid &&
    note.trim().length > 0 &&
    !m.isPending;

  async function handleSubmit(): Promise<void> {
    if (!sourceRow) return;
    setErrorMsg(null);
    try {
      const r = await m.mutateAsync({
        variant_id: sourceRow.variant_id,
        from_warehouse_id: sourceRow.warehouse_id,
        to_warehouse_id: targetWarehouseId,
        quantity: qty,
        reason: note.trim(),
      });
      onSuccess(
        t('transferForm.successMessage', {
          qty,
          from: shortId(sourceRow.warehouse_id),
          to: shortId(targetWarehouseId),
          code: r.transfer_id.slice(-6),
        }),
      );
    } catch (err) {
      const e = err as InventoryApiError;
      setErrorMsg(e.message);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="inventory-transfer-form-modal"
      /* dark-ok: modal scrim, translucent black reads correctly in both modes */
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="w-full max-w-md rounded-xl border border-pw-border bg-pw-bg p-6 shadow-xl">
        <h2 className="text-lg font-semibold text-pw-text">{t('transferForm.title')}</h2>
        <p className="mt-1 text-xs text-pw-text-muted">
          {t('transferForm.intro')}
        </p>

        <div className="mt-4 flex flex-col gap-3">
          <div className="relative flex flex-col gap-1">
            <span className="text-sm font-medium text-pw-text">{t('transferForm.sourceLabel')}</span>
            <Input
              type="search"
              data-testid="inventory-transfer-source-search"
              value={
                sourceRow && !showSourceDropdown
                  ? `${sourceRow.product_name ?? shortId(sourceRow.variant_id)} · ${
                      sourceRow.variant_label ?? '—'
                    } · ${sourceRow.warehouse_name ?? shortId(sourceRow.warehouse_id)} (${t('transferForm.sourceItemStock')} ${
                      sourceRow.on_hand
                    })`
                  : sourceSearch
              }
              placeholder={t('transferForm.sourceSearchPlaceholder')}
              onFocus={() => setShowSourceDropdown(true)}
              onChange={(e) => {
                setSourceSearch(e.target.value);
                setShowSourceDropdown(true);
                if (stockId) setStockId('');
              }}
              className="border-pw-border bg-pw-bg text-pw-text"
            />
            {showSourceDropdown ? (
              <div
                role="listbox"
                data-testid="inventory-transfer-source-list"
                className="absolute top-full z-10 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-pw-border bg-pw-bg shadow-lg"
              >
                {stockQ.isLoading ? (
                  <p className="px-3 py-2 text-xs text-pw-text-muted">{t('transferForm.sourceLoading')}</p>
                ) : filteredSourceRows.length === 0 ? (
                  <p className="px-3 py-2 text-xs text-pw-text-muted">
                    {t('transferForm.sourceNoMatch')}
                  </p>
                ) : (
                  filteredSourceRows.map((r) => (
                    <Button
                      key={r.stock_id}
                      type="button"
                      variant="ghost"
                      onClick={() => {
                        setStockId(r.stock_id);
                        setTargetWarehouseId('');
                        setShowSourceDropdown(false);
                        setSourceSearch('');
                      }}
                      className="flex h-auto w-full flex-col items-start justify-start gap-0.5 rounded-none px-3 py-2 text-left text-sm font-normal hover:bg-pw-surface"
                    >
                      <span className="font-medium text-pw-text">
                        {r.product_name ?? shortId(r.variant_id)}
                        {r.variant_sku ? (
                          <span className="ml-1 text-xs text-pw-text-muted">
                            ({r.variant_sku})
                          </span>
                        ) : null}
                      </span>
                      <span className="text-xs text-pw-text-muted">
                        {r.variant_label ?? '—'} · {r.warehouse_name ?? shortId(r.warehouse_id)} ·
                        {' '}{t('transferForm.sourceItemStock')} {r.on_hand}
                      </span>
                    </Button>
                  ))
                )}
              </div>
            ) : null}
            {!stockQ.isLoading && stockRows.length === 0 ? (
              <span className="text-xs text-yellow-700">
                {t('transferForm.sourceEmptyWarn')}
              </span>
            ) : null}
          </div>

          <label className="flex flex-col gap-1">
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-medium text-pw-text">{t('transferForm.targetLabel')}</span>
              <Link
                to="/settings/warehouses"
                onClick={onClose}
                className="text-xs text-pw-primary underline hover:text-pw-primary-strong"
                data-testid="inventory-transfer-target-manage-link"
              >
                {t('transferForm.targetManageLink')}
              </Link>
            </div>
            <select
              value={targetWarehouseId}
              onChange={(e) => setTargetWarehouseId(e.target.value)}
              disabled={!sourceRow}
              data-testid="inventory-transfer-target-select"
              className="h-10 rounded-md border border-pw-border bg-pw-bg px-3 text-sm text-pw-text disabled:opacity-50"
            >
              <option value="">{t('transferForm.targetPlaceholder')}</option>
              {targetOptions.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.label}
                </option>
              ))}
            </select>
            <span className="text-xs text-pw-text-muted">
              {t('transferForm.targetHint')}
            </span>
            {sourceRow && targetOptions.length === 0 ? (
              <span className="text-xs text-yellow-700">
                {t('transferForm.targetSingleWarehouseWarn')}{' '}
                <Link
                  to="/settings/warehouses"
                  className="text-pw-primary underline hover:text-pw-primary-strong"
                  onClick={onClose}
                >
                  {t('transferForm.targetSingleWarehouseLink')}
                </Link>{' '}
                {t('transferForm.targetSingleWarehouseTail')}
              </span>
            ) : null}
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium text-pw-text">{t('transferForm.qtyLabel')}</span>
            <Input
              type="number"
              min={1}
              step={1}
              value={qtyStr}
              onChange={(e) => setQtyStr(e.target.value)}
              data-testid="inventory-transfer-qty-input"
              className="border-pw-border bg-pw-bg text-pw-text"
            />
            {sourceRow ? (
              <span className="text-xs text-pw-text-muted">
                {t('transferForm.qtyAvailableAtSource', { available: sourceRow.available })}
              </span>
            ) : null}
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium text-pw-text">{t('transferForm.noteLabel')}</span>
            <textarea
              rows={3}
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="inventory-transfer-note-input"
              className="rounded-md border border-pw-border bg-pw-bg px-3 py-2 text-sm text-pw-text"
            />
          </label>
        </div>

        {errorMsg ? (
          <p
            data-testid="inventory-transfer-error"
            className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            {errorMsg}
          </p>
        ) : null}

        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            data-testid="inventory-transfer-cancel"
            onClick={onClose}
            className="rounded-md border border-pw-border bg-pw-surface px-4 py-2 text-sm font-medium text-pw-text hover:bg-pw-bg"
          >
            {t('transferForm.cancel')}
          </Button>
          <Button
            type="button"
            data-testid="inventory-transfer-submit"
            disabled={!canSubmit}
            onClick={() => void handleSubmit()}
            className="rounded-md bg-pw-primary px-4 py-2 text-sm font-semibold text-white hover:bg-pw-primary-strong disabled:cursor-not-allowed disabled:opacity-50"
          >
            {m.isPending ? t('transferForm.submitting') : t('transferForm.submit')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default TransferFormModal;
